import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, RefreshCw, GitBranch } from 'lucide-react';
import { getApiBase } from '../apiBase';
import BpmRuleAuthoringDrawer from './BpmRuleAuthoringDrawer';
import BpmConfiguredRulesView from './BpmConfiguredRulesView';
import ABSENCE_APPROVAL_RULES_SNAPSHOT from './absenceApprovalRulesSnapshot';
import { TIMECARD_RULESETS } from './timecardApprovalRulesSnapshot';
import {
  GLOBAL_ABSENCE_TASK_QUERY,
  ABSENCE_REFERENCE_EXTRACT,
  segmentsFromMeta,
  cloneDefaultSegments,
  snapshotRuleToDesign,
} from './bpmApprovalFlowShared';

const API = axios.create({
  baseURL: getApiBase(),
  // configured-approvals queries tenant approval composites via BIP, which can
  // take 30-60s per task; match the other BPM calls (was 45s and timed out).
  timeout: 120000,
  withCredentials: true,
});

export { GLOBAL_ABSENCE_TASK_QUERY };

function normalizeExtractPayload(data) {
  if (!data) return null;
  const arrays = data.arrays || {};
  const { sqlError: _sqlError, ...safe } = data;
  return {
    ...safe,
    source: data.source,
    conditions: arrays.conditions || data.conditions || [],
    flow: arrays.flow || data.flow,
    actions: arrays.actions || [],
    rules: arrays.rules || data.rules || [],
    sqlRows: data.sqlRows || arrays.rules || [],
    amxSource: data.amxSource || data.source,
    task: data.task,
    message: data.message,
    rowCounts: data.rowCounts,
    rawXmlLength: data.rawXmlLength,
    recipeId: data.recipeId,
  };
}

function referenceMetaForTask(taskKey, taskQuery) {
  const base = ABSENCE_REFERENCE_EXTRACT;
  return {
    ...base,
    task: {
      ...base.task,
      name: taskQuery.name || base.task.name,
      code: taskQuery.code || base.task.code,
      category: taskQuery.category || base.task.category,
      taskKey,
    },
  };
}

function classifyApprovalApiError(err) {
  const status = err?.response?.status;
  const msg = String(err?.message || '');
  const body = err?.response?.data;
  const bodyText =
    typeof body === 'string' ? body : body?.error || body?.message || '';

  if (status === 404 || (typeof bodyText === 'string' && /cannot get \/api/i.test(bodyText))) {
    return {
      kind: 'stale_api',
      notice:
        'Approval API returned 404 — Node on port 3010 is an old build (missing /api/hcm/configured-approvals). From C:\\dev\\AI run: .\\scripts\\restart-dev.ps1 -StartClient then hard-refresh. Check curl.exe http://localhost:3010/api/health shows apiBuild bpm-routes-v2.',
    };
  }
  if (status === 502 || status === 504 || status === 503) {
    return {
      kind: 'proxy',
      notice: `Approval API proxy error (HTTP ${status}) — set client REACT_APP_PROXY_TARGET=http://localhost:3010 to match .env PORT, restart API, then npm start in client/.`,
    };
  }
  if (!err?.response && /ECONNREFUSED|ENOTFOUND|network/i.test(msg)) {
    return {
      kind: 'unreachable',
      notice:
        'Cannot reach the API (connection refused) — start node server.js on port 3010 from C:\\dev\\AI. Showing reference absence flow.',
    };
  }
  return null;
}

/**
 * Fetches /api/hcm/configured-approvals with editable flow sequence (Steps A–C).
 */
export default function BpmRuleFlowPreview({
  taskKey = 'global-absence-recording-single-assignment',
  taskQuery = GLOBAL_ABSENCE_TASK_QUERY,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);
  const [ruleSegments, setRuleSegments] = useState(() => cloneDefaultSegments());
  const [showAuthor, setShowAuthor] = useState(false);
  const [seedDesign, setSeedDesign] = useState(null);

  const mergedQuery = useMemo(
    () => ({ ...GLOBAL_ABSENCE_TASK_QUERY, ...(taskQuery || {}) }),
    [taskQuery],
  );
  const queryKey = JSON.stringify(mergedQuery);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const taskQueryParsed = queryKey ? JSON.parse(queryKey) : {};
    try {
      const params = {
        taskKey,
        processName: taskQueryParsed.name || mergedQuery.name,
        ...taskQueryParsed,
      };
      let res = await API.get('/api/hcm/configured-approvals', { params });
      let normalized = normalizeExtractPayload(res.data);
      if (!normalized?.flow?.nodes?.length && !normalized?.conditions?.length) {
        res = await API.get('/api/copilot/approvals/extract', { params });
        normalized = normalizeExtractPayload(res.data);
      }
      if (normalized && (res.data?.ok !== false || normalized.flow || normalized.conditions?.length)) {
        setMeta(normalized);
        return;
      }
      setMeta(referenceMetaForTask(taskKey, taskQueryParsed));
    } catch (extractErr) {
      const classified = classifyApprovalApiError(extractErr);
      if (classified) {
        setMeta(referenceMetaForTask(taskKey, taskQueryParsed));
        return;
      }

      const status = extractErr.response?.status;
      if (status === 403 || status === 401) {
        setError(
          status === 401 ? 'Sign in to load approval rules.' : 'You do not have access to approval rules.',
        );
        setMeta(referenceMetaForTask(taskKey, taskQueryParsed));
        return;
      }

      setMeta(referenceMetaForTask(taskKey, taskQueryParsed));
    } finally {
      setLoading(false);
    }
  }, [taskKey, queryKey, mergedQuery.name]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!meta) return;
    const fromMeta = segmentsFromMeta(meta);
    if (fromMeta?.length) {
      setRuleSegments(fromMeta.map((s) => ({ ...s, actors: [...(s.actors || [])] })));
    }
  }, [meta]);

  const categoryStr = String(mergedQuery.category || '');
  const nameStr = String(mergedQuery.name || '');
  const taskKeyStr = String(taskKey || '');
  const isTimecardTask =
    /time|labor/i.test(categoryStr) ||
    /time\s*card|timecard|payroll\s*time/i.test(nameStr) ||
    /time-?card/i.test(taskKeyStr) ||
    TIMECARD_RULESETS.some((rs) => rs.task.taskKey === taskKey);
  const isAbsenceTask =
    !isTimecardTask &&
    (taskKey === ABSENCE_APPROVAL_RULES_SNAPSHOT.task.taskKey ||
      /absence|leave/i.test(categoryStr) ||
      /absence|leave/i.test(nameStr));

  return (
    <section
      className="mt-4 shrink-0 rounded-xl border border-slate-200 bg-white shadow-sm"
      aria-labelledby="bpm-rule-flow-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <GitBranch className="h-5 w-5 text-cyan-600 shrink-0" />
          <div className="min-w-0">
            <h2 id="bpm-rule-flow-heading" className="text-sm font-semibold text-slate-800">
              Configured rule flow
            </h2>
            <p className="text-xs text-slate-500 truncate">
              {meta?.task?.name || mergedQuery.name || taskKey}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {loading && !meta && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading approval rules…
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}
      </div>

      {isAbsenceTask && (
        <div className="px-5 pb-5">
          <BpmConfiguredRulesView
            ruleset={ABSENCE_APPROVAL_RULES_SNAPSHOT}
            onCopyRule={(rule) => {
              setSeedDesign(
                snapshotRuleToDesign(
                  rule,
                  ABSENCE_APPROVAL_RULES_SNAPSHOT.task,
                  mergedQuery.category || 'absence'
                )
              );
              setShowAuthor(true);
            }}
          />
        </div>
      )}

      {isTimecardTask && (
        <div className="space-y-3 px-5 pb-5">
          {TIMECARD_RULESETS.map((rs) => (
            <BpmConfiguredRulesView
              key={rs.task.taskKey}
              ruleset={rs}
              onCopyRule={(rule) => {
                setSeedDesign(snapshotRuleToDesign(rule, rs.task, 'time_labor'));
                setShowAuthor(true);
              }}
            />
          ))}
        </div>
      )}

      <BpmRuleAuthoringDrawer
        open={showAuthor}
        onClose={() => {
          setShowAuthor(false);
          setSeedDesign(null);
        }}
        api={API}
        task={{
          name: meta?.task?.name || mergedQuery.name,
          code: meta?.task?.code || mergedQuery.code,
          category: mergedQuery.category,
          taskKey: meta?.task?.taskKey || taskKey,
        }}
        meta={meta}
        segments={ruleSegments}
        seedDesign={seedDesign}
      />
    </section>
  );
}
